/**
 * EXPO-STAND — Contractor Profile & Portfolio
 * Reusable image upload component for all asset types:
 *   avatar, cover, logo, portfolio
 *
 * Usage:
 *   <ProfileImageUpload
 *     value={member.avatar_url}
 *     onChange={(path) => updateMember({ avatar_url: path })}
 *     onDelete={() => updateMember({ avatar_url: null })}
 *     assetType="avatar"
 *     entityId={member.id}
 *     shape="circle"          // "circle" | "rect"
 *     label="Team Member Photo"
 *   />
 *
 * Value convention:
 *   - null / empty  → show placeholder
 *   - starts with "expostand/" → proxied via /api/contractor/profile/assets/{path}
 *   - starts with "http" → legacy direct URL (backward compatible)
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, RefreshCw, User, ImageIcon, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 10;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/** Convert a stored asset_path or legacy URL to a displayable src. */
export function resolveAssetUrl(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  // New storage path
  return `${API}/api/contractor/profile/assets/${value}`;
}

export function ProfileImageUpload({
  value,
  onChange,
  onDelete,
  assetType = 'avatar',
  entityId = null,
  shape = 'rect',
  label = 'Upload Image',
  aspectClass = 'aspect-square',
  className = '',
}) {
  const { token } = useAuth();
  const inputRef = useRef(null);

  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [localPreview, setLocalPreview] = useState(null); // before upload completes

  const displaySrc = localPreview || resolveAssetUrl(value);

  // ── Validation ─────────────────────────────────────────────────────────────
  function validateFile(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Unsupported format. Use JPG, PNG, or WEBP.`;
    }
    if (file.size > MAX_SIZE_BYTES) {
      return `File too large. Maximum size is ${MAX_SIZE_MB} MB.`;
    }
    return null;
  }

  // ── Upload ──────────────────────────────────────────────────────────────────
  const uploadFile = useCallback(async (file) => {
    const err = validateFile(file);
    if (err) { setError(err); return; }

    setError(null);
    setUploading(true);
    setProgress(10);

    // Local preview (immediate feedback)
    const reader = new FileReader();
    reader.onload = (e) => setLocalPreview(e.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);

    let url = `${API}/api/contractor/profile/upload?asset_type=${assetType}`;
    if (entityId) url += `&entity_id=${encodeURIComponent(entityId)}`;

    try {
      setProgress(40);
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      setProgress(80);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${res.status})`);
      }

      const data = await res.json();
      setProgress(100);
      setLocalPreview(null); // clear preview — use persisted path
      onChange(data.asset_path);
    } catch (e) {
      setLocalPreview(null);
      setError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [assetType, entityId, token, onChange]);

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  // ── File input ─────────────────────────────────────────────────────────────
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = ''; // allow re-selecting same file
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!value || value.startsWith('http')) {
      // Legacy URL — just clear from state
      onChange(null);
      onDelete?.();
      return;
    }
    try {
      await fetch(`${API}/api/contractor/profile/assets?asset_path=${encodeURIComponent(value)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Soft-delete failure is non-blocking; clear from UI anyway
    }
    onChange(null);
    onDelete?.();
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const isCircle = shape === 'circle';
  const containerCls = isCircle
    ? 'relative flex flex-col items-center gap-2'
    : `relative flex flex-col gap-2 ${className}`;

  const imgCls = isCircle
    ? 'w-24 h-24 rounded-full object-cover object-center border-2 border-gray-200 bg-gray-100'
    : `w-full ${aspectClass} rounded-xl object-cover object-center border border-gray-200 bg-gray-100`;

  const dropzoneCls = [
    'flex flex-col items-center justify-center gap-2 cursor-pointer transition-all',
    'border-2 border-dashed rounded-xl text-center p-4',
    isCircle ? 'w-24 h-24 rounded-full' : `w-full ${aspectClass}`,
    dragging
      ? 'border-green-400 bg-green-50'
      : 'border-gray-200 hover:border-green-300 hover:bg-gray-50',
  ].join(' ');

  return (
    <div className={containerCls}>
      {/* Image or drop zone */}
      {displaySrc ? (
        <div className={`relative ${isCircle ? 'w-24 h-24' : 'w-full'}`}>
          <img
            src={displaySrc}
            alt={label}
            className={imgCls}
            onError={(e) => { e.target.src = ''; e.target.onerror = null; }}
          />
          {/* Overlay controls */}
          {!uploading && (
            <div className={`absolute inset-0 flex items-center justify-center gap-1.5 bg-black/40 opacity-0 hover:opacity-100 transition-opacity ${isCircle ? 'rounded-full' : 'rounded-xl'}`}>
              <button
                type="button"
                title="Change image"
                onClick={() => inputRef.current?.click()}
                className="p-1.5 bg-white rounded-full text-gray-700 hover:text-green-700 shadow"
                data-testid={`change-image-btn-${entityId || assetType}`}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Remove image"
                onClick={handleDelete}
                className="p-1.5 bg-white rounded-full text-gray-700 hover:text-red-600 shadow"
                data-testid={`delete-image-btn-${entityId || assetType}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {/* Upload progress bar */}
          {uploading && (
            <div className={`absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-2 ${isCircle ? 'rounded-full' : 'rounded-xl'}`}>
              <Loader2 className="h-5 w-5 text-white animate-spin" />
              <div className="w-3/4 bg-white/30 rounded-full h-1">
                <div
                  className="bg-white h-1 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={dropzoneCls}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid={`upload-dropzone-${entityId || assetType}`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 text-green-600 animate-spin" />
              <span className="text-xs text-green-600">{progress}%</span>
            </>
          ) : (
            <>
              {isCircle
                ? <User className="h-8 w-8 text-gray-300" />
                : <ImageIcon className="h-7 w-7 text-gray-300" />
              }
              <span className="text-xs text-gray-400 leading-tight">
                {dragging ? 'Drop here' : 'Click or drag & drop'}
              </span>
              {!isCircle && (
                <span className="text-[10px] text-gray-300">JPG · PNG · WEBP · max {MAX_SIZE_MB}MB</span>
              )}
            </>
          )}
        </div>
      )}

      {/* Upload / Change button (below dropzone when no image) */}
      {!displaySrc && !uploading && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 font-medium mt-0.5"
          data-testid={`upload-btn-${entityId || assetType}`}
        >
          <Upload className="h-3.5 w-3.5" /> {label}
        </button>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-600 mt-1 max-w-full" data-testid="upload-error">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        onChange={onFileChange}
        data-testid={`file-input-${entityId || assetType}`}
      />
    </div>
  );
}

export default ProfileImageUpload;
